import { yieldToPaint } from "@coreapp/utils/yieldToPaint";
import { useState } from "react";
import { GetDoc, GetWSMgr } from "@/Utils/StateLib";
import { UpdateUser } from "@/appComponents/Blocks/Modals/SettingsModal/AccountSettings/ManageUsers/Utils";
import {
  groupSchema,
  containerTypesEnum,
  SetActiveGroup,
  ValidGroupField,
  ValidGroupName,
  getGroupById,
  getGroupIndexById,
  getValidGroupId
} from "./Grouping.utils";
import { QUserMgmtRFC } from "@coreapp/CoreApp/QUserMgmtRFC";
import { QRulePair } from "@coreapp/CoreApp/QRulePair";
import { appstr } from "@coreapp/utils/appstr";
import { enumBoxStyleSet, enumContainerType, enumHierarchyTypes } from "@coreapp/constants";
import { useBlockerContext } from "@/providers/BlockerProvider";
import { useTopicBlocker } from "@/hooks/useTopicBlocker";
import { TOAST_STATUS, useToast } from "@/providers/ToastProvider";
import { createBoldToastMessage } from "@/appComponents/Blocks/Cards/ToastContainer/utils";
import {
  computeRulePairErrors,
  intersectErrors
} from "@/appComponents/Blocks/Composites/RuleInput/ruleErrors";

export const useGroupingController = () => {
  const wsMgr = GetWSMgr();
  const doc = GetDoc();
  const user = wsMgr.GetUserInfo();
  const { shouldBlock } = useBlockerContext();
  const { addToast } = useToast();
  const [groups, setGroups] = useState(user.GetChartGroups() ?? []);
  const [editState, setEditState] = useState({
    showModal: false,
    newGroupMode: false,
    editingGroup: null
  });
  const [nameErrorShown, setNameErrorShown] = useState(false);
  const [fieldErrorShown, setFieldErrorShown] = useState(false);
  const [rulePairError, setRulePairError] = useState(null);
  const [lastCustomGroupStyle, setLastCustomGroupStyle] = useState(null);
  const [lastManagerCustomGroupStyle, setLastManagerCustomGroupStyle] = useState(null);
  const [originalGroup, setOriginalGroup] = useState(null);

  const nameErrorMessage = (() => {
    const name = editState.editingGroup?.name ?? "";
    if (!name.trim()) return appstr("This field is required");
    if (editState.newGroupMode && !ValidGroupName(groups, name)) return appstr("formulaerrdupname");
    return null;
  })();

  const fieldErrorMessage = (() => {
    const field = editState.editingGroup?.actions?.grouping?.fGroupingField;
    if (!field) return appstr("This field is required");
    return null;
  })();

  const isDirty =
    editState.showModal &&
    editState.editingGroup != null &&
    originalGroup != null &&
    JSON.stringify(editState.editingGroup) !== JSON.stringify(originalGroup);

  const activeGroup = doc && doc.GetActiveGroup();
  const activeGroupID = activeGroup ? activeGroup.activeGroupID : null;
  const activeUser = activeGroup ? activeGroup.user : null;

  const ToggleGroup = idx => {
    const wsMgr = GetWSMgr();
    const doc = GetDoc();
    const selectedGroup = groups[idx];
    let ungroupOnly = false;

    if (activeGroupID === selectedGroup.id && activeUser === wsMgr.GetUserName()) {
      ungroupOnly = true;
      localStorage.setItem("activeGroup", null);
      user.SetLastActiveGroup(activeGroupID);
      QUserMgmtRFC.UpdateChartRules(wsMgr, {
        activeGrouping: {
          user: null,
          activeGroupID: null
        }
      });
    } else if (ValidGroupField(selectedGroup)) {
      const activeGroupData = { user: wsMgr.GetUserName(), activeGroupID: selectedGroup.id };
      localStorage.setItem("activeGroup", JSON.stringify(activeGroupData));
      user.SetLastActiveGroup(null);
      QUserMgmtRFC.UpdateChartRules(wsMgr, {
        activeGrouping: activeGroupData
      });
    }

    // Defer ApplyChartGrouping to after React processes state updates from UpdateChartRules above.
    // React re-renders can trigger canvas resize/redraw operations that would clear the canvas
    // after ApplyChartGrouping renders it. yieldToPaint (rAF + setTimeout) ensures the chart
    // render is the final operation on the canvas.
    yieldToPaint().then(() => {
      doc.ApplyChartGrouping(selectedGroup, ungroupOnly);
    });
  };

  const UpdateGroups = async updatedGroups => {
    const wsMgr = GetWSMgr();
    const data = { userID: wsMgr.GetUserName(), user: [{ chartGroups: updatedGroups }] };
    const { result } = await UpdateUser({ theData: data });
    if (result) setGroups(updatedGroups);
  };

  const onCreateGroup = () => {
    const newGroup = structuredClone(groupSchema);
    newGroup.id = getValidGroupId(groups);
    newGroup.name = "New Group";
    setLastCustomGroupStyle(null);
    setLastManagerCustomGroupStyle(null);
    setEditState({ showModal: true, editingGroup: newGroup, newGroupMode: true });
    setOriginalGroup(structuredClone(newGroup));
    setNameErrorShown(false);
    setFieldErrorShown(false);
    setRulePairError(null);
  };

  const onEditGroup = groupId => {
    const selectedGroup = getGroupById(groups, groupId);
    const existingStyle = selectedGroup?.actions?.grouping?.fGroupingBoxStyle;
    const isCustom =
      existingStyle != null &&
      existingStyle !== containerTypesEnum.groupFramed &&
      existingStyle !== containerTypesEnum.groupHeader &&
      existingStyle !== containerTypesEnum.nameOnly;
    setLastCustomGroupStyle(isCustom ? existingStyle : null);
    const existingMgrBoxStyle = selectedGroup?.actions?.grouping?.fManagerGroupingBoxStyle;
    setLastManagerCustomGroupStyle(existingMgrBoxStyle ?? null);
    setEditState({ ...editState, showModal: true, editingGroup: selectedGroup });
    setOriginalGroup(structuredClone(selectedGroup));
    setNameErrorShown(false);
    setFieldErrorShown(false);
    setRulePairError(null);
  };

  const setGroupField = (key, value) => {
    const newGroup = structuredClone(editState.editingGroup);
    if (key === "name") {
      newGroup.name = value;
    } else if (key === "conditionalEnabled") {
      newGroup.actions.grouping.conditionalEnabled = value;
      if (!value) newGroup.pairs = [];
    } else {
      newGroup.actions.grouping[key] = value;
    }
    setEditState({ ...editState, editingGroup: newGroup });
  };

  const onDeleteGroup = async groupId => {
    if (groupId === activeGroupID) {
      const doc = GetDoc();
      SetActiveGroup(null);
      QUserMgmtRFC.UpdateChartRules(wsMgr, {
        activeGrouping: {
          user: null,
          activeGroupID: null
        }
      });
      const lastGroup = user.GetChartGroupByID(groupId);
      yieldToPaint().then(() => {
        doc.ApplyChartGrouping(lastGroup, true);
      });
    }
    const newGroups = [...groups];
    newGroups.splice(getGroupIndexById(groups, groupId), 1);
    await UpdateGroups(newGroups);
  };

  const toggleGroupStyle = boxStyleId => {
    const newGroup = structuredClone(editState.editingGroup);
    const groupingObj = newGroup.actions.grouping;
    if (boxStyleId === containerTypesEnum.groupHeader) {
      groupingObj.fChildrenBoxStyle = containerTypesEnum.nameOnly;
      groupingObj.fChildrenBoxStyleEnabled = true;
    } else {
      groupingObj.fChildrenBoxStyle = null;
      groupingObj.fChildrenBoxStyleEnabled = false;
    }
    groupingObj.fGroupingBoxStyle = boxStyleId;
    newGroup.actions.grouping = groupingObj;
    setEditState({ ...editState, editingGroup: newGroup });
  };

  const setEmployeeBoxStyle = styleId => {
    const newGroup = structuredClone(editState.editingGroup);
    newGroup.actions.grouping.fChildrenBoxStyle = styleId;
    newGroup.actions.grouping.fChildrenBoxStyleEnabled = styleId != null;
    setEditState({ ...editState, editingGroup: newGroup });
  };

  const setCustomGroupStyle = styleId => {
    const newGroup = structuredClone(editState.editingGroup);
    const groupingObj = newGroup.actions.grouping;
    let resolvedId;
    if (styleId === undefined) {
      // Called from the Custom radio: resolve to last used style, or first real available style.
      const firstReal = getCustomGroupStyles().find(s => s.value != null)?.value;
      resolvedId = lastCustomGroupStyle ?? firstReal;
    } else {
      // Called from the dropdown (null = "Default", or a real style ID).
      resolvedId = styleId;
    }
    groupingObj.fGroupingBoxStyle = resolvedId;
    // Clear fChildrenBoxStyle only if it was forced to nameOnly by the Condensed path.
    // Preserve any employee box style the user explicitly chose.
    if (groupingObj.fChildrenBoxStyle === containerTypesEnum.nameOnly) {
      groupingObj.fChildrenBoxStyle = null;
      groupingObj.fChildrenBoxStyleEnabled = false;
    }
    newGroup.actions.grouping = groupingObj;
    // Update lastCustomGroupStyle only when a real ID is resolved (not null/"Default").
    if (resolvedId != null) setLastCustomGroupStyle(resolvedId);
    setEditState({ ...editState, editingGroup: newGroup });
  };

  const setManagerGroupStyle = style => {
    const newGroup = structuredClone(editState.editingGroup);
    newGroup.actions.grouping.fManagerGroupingStyle = style;
    if (style !== "custom") {
      newGroup.actions.grouping.fManagerGroupingBoxStyle = null;
      newGroup.actions.grouping.fManagerChildrenBoxStyle = null;
      newGroup.actions.grouping.fManagerChildrenBoxStyleEnabled = false;
    }
    setEditState({ ...editState, editingGroup: newGroup });
  };

  const setManagerCustomGroupStyle = styleId => {
    const newGroup = structuredClone(editState.editingGroup);
    const groupingObj = newGroup.actions.grouping;
    let resolvedId;
    if (styleId === undefined) {
      const firstReal = getCustomGroupStyles().find(s => s.value != null)?.value;
      resolvedId = lastManagerCustomGroupStyle ?? firstReal;
    } else {
      resolvedId = styleId;
    }
    groupingObj.fManagerGroupingStyle = "custom";
    groupingObj.fManagerGroupingBoxStyle = resolvedId;
    newGroup.actions.grouping = groupingObj;
    if (resolvedId != null) setLastManagerCustomGroupStyle(resolvedId);
    setEditState({ ...editState, editingGroup: newGroup });
  };

  const setManagerChildrenBoxStyle = styleId => {
    const newGroup = structuredClone(editState.editingGroup);
    newGroup.actions.grouping.fManagerChildrenBoxStyle = styleId;
    newGroup.actions.grouping.fManagerChildrenBoxStyleEnabled = styleId != null;
    setEditState({ ...editState, editingGroup: newGroup });
  };

  const getCustomGroupStyles = () => {
    const bsMgr = wsMgr.GetBoxStylesMgr();
    const priorSet = bsMgr.CurrentBoxStyleSet();
    const styles = [{ value: null, label: appstr("Default to last used or only created") }];
    [enumBoxStyleSet.userboxstyles, enumBoxStyleSet.sharedboxstyles].forEach(set => {
      bsMgr.SwitchBoxStyles(set);
      const n = bsMgr.GetNumLayouts();
      for (let i = 0; i < n; i++) {
        const layout = bsMgr.GetIndexLayout(i);
        if (
          layout.GetContainerType() === enumContainerType.standardmrb &&
          layout.GetHierarchyType() === enumHierarchyTypes.grouping
        ) {
          styles.push({ value: layout.GetBoxStyleID(), label: layout.GetBoxStyleName() });
        }
      }
    });
    bsMgr.SwitchBoxStyles(priorSet);
    return styles;
  };

  const getEmployeeBoxStyles = () => {
    const bsMgr = wsMgr.GetBoxStylesMgr();
    const priorSet = bsMgr.CurrentBoxStyleSet();
    const styles = [{ value: null, label: appstr("Default to view's box style") }];
    [
      enumBoxStyleSet.systemboxstyles,
      enumBoxStyleSet.userboxstyles,
      enumBoxStyleSet.sharedboxstyles
    ].forEach(set => {
      bsMgr.SwitchBoxStyles(set);
      const n = bsMgr.GetNumLayouts();
      for (let i = 0; i < n; i++) {
        const layout = bsMgr.GetIndexLayout(i);
        if (
          layout.GetContainerType() === enumContainerType.nonmrb &&
          layout.GetHierarchyType() === enumHierarchyTypes.employees
        ) {
          styles.push({ value: layout.GetBoxStyleID(), label: layout.GetBoxStyleName() });
        }
      }
    });
    bsMgr.SwitchBoxStyles(priorSet);
    return styles;
  };

  const updateConditonalRule = rule => {
    const newGroup = structuredClone(editState.editingGroup);
    newGroup.pairs = [rule.OutputToJSON()];
    setEditState({ ...editState, editingGroup: newGroup });
    setRulePairError(prev => {
      if (!prev) return null;
      return intersectErrors(prev, computeRulePairErrors(rule));
    });
  };

  const handleSave = async () => {
    const newGroup = structuredClone(editState.editingGroup);

    if (nameErrorMessage) setNameErrorShown(true);
    if (fieldErrorMessage) setFieldErrorShown(true);

    let rulePairErrors = null;
    const conditionalEnabled = newGroup.actions?.grouping?.conditionalEnabled;
    if (conditionalEnabled) {
      const pairs = newGroup.pairs || [];
      const ruleToValidate = new QRulePair();
      if (pairs.length) {
        const p = pairs[0];
        ruleToValidate.InitRulePair(p.fieldName, p.fieldID, null, p.operator, p.value);
      }
      const errors = computeRulePairErrors(ruleToValidate);
      if (errors.hasError) {
        rulePairErrors = errors;
        setRulePairError(errors);
      }
    }

    if (nameErrorMessage || fieldErrorMessage || rulePairErrors) return;

    const updatedGroups = [...groups];
    const updateIdx = getGroupIndexById(groups, newGroup.id);
    if (updateIdx !== -1) updatedGroups[updateIdx] = newGroup;
    else updatedGroups.push(newGroup);
    setEditState({ editingGroup: null, newGroupMode: false, showModal: false });
    // Await so user info is authoritative before the canvas re-applies.
    await UpdateGroups(updatedGroups);
    addToast({
      ...createBoldToastMessage(
        editState.newGroupMode ? "Entity group created" : "Entity changes saved",
        { entity: newGroup.name ?? "" }
      ),
      status: TOAST_STATUS.SUCCESS,
      timeoutMS: 5000
    });
    if (newGroup.id === activeGroupID) {
      const doc = GetDoc();
      await yieldToPaint();
      doc.ApplyChartGrouping(newGroup);
    }
  };

  const groupIsActive = groupId => {
    const wsMgr = GetWSMgr();
    const user = wsMgr.GetUserName();
    return groupId === activeGroupID && user === activeUser;
  };

  const getIsNewGroupMode = () => editState.newGroupMode;

  const goBack = () => {
    setEditState({ showModal: false, newGroupMode: false, editingGroup: null });
    setNameErrorShown(false);
    setFieldErrorShown(false);
    setRulePairError(null);
  };

  const shouldBlockGroupEdit = () => ({
    doBlock: isDirty,
    alertParams: {
      title: appstr("Unsaved changes detected"),
      message: appstr("saveconfirm3"),
      confirmText: appstr("Save Changes"),
      denyText: appstr("Discard Changes"),
      onConfirm: handleSave,
      onDeny: goBack
    }
  });

  const handleGoBack = (ignoreBlocker = false) => {
    if (!ignoreBlocker) {
      const onProceed = () => handleGoBack(true);
      if (shouldBlock("editGrouping", onProceed)) return;
    }
    goBack();
  };

  useTopicBlocker("editGrouping", shouldBlockGroupEdit);
  useTopicBlocker("rightSidebarChangeMode", shouldBlockGroupEdit);
  useTopicBlocker("refreshDocument", shouldBlockGroupEdit);

  return {
    groups,
    activeGroupID,
    onCreateGroup,
    onDeleteGroup,
    ToggleGroup,
    onEditGroup,
    setGroupField,
    toggleGroupStyle,
    setEmployeeBoxStyle,
    setCustomGroupStyle,
    setManagerGroupStyle,
    setManagerCustomGroupStyle,
    setManagerChildrenBoxStyle,
    getCustomGroupStyles,
    getEmployeeBoxStyles,
    updateConditonalRule,
    handleSave,
    setEditState,
    editState,
    nameErrorShown,
    nameErrorMessage,
    fieldErrorShown,
    fieldErrorMessage,
    rulePairError,
    groupIsActive,
    getIsNewGroupMode,
    handleGoBack,
    isDirty
  };
};
